import { warnAndNotify } from '@execonline-inc/error-reporting.private';
import { missingLinkError } from '@execonline-inc/links.private';
import { toTask } from '@execonline-inc/maybe-adapter';
import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { Task } from 'taskarian';
import PublishingStore from '.';
import { callApi } from '../../../../../../../../Appy';
import { findLinkBy } from '../../../../../../../../Links';
import ReactionComponent from '../../../../../../../../ReactionComponent';
import { Link } from '../../../../../../../../Resource/Types';
import { fromNullableT } from '../../../../../../../../TaskExt';
import { learningCollectionPublishResourceDecoder } from './Decoders';
import {
  LearningCollectionPublishResource,
  PublishingError,
  PublishingState,
  isPublished,
} from './Types';

interface Props {
  store: PublishingStore;
}

interface LearningCollectionPublishingOut {
  published: boolean;
}

const togglePublishStatus = (publishedAt: Maybe<Date>): boolean => !isPublished(publishedAt);

export const learningCollectionPublishingEncoder = (
  publishedAt: Maybe<Date>,
): LearningCollectionPublishingOut => ({
  published: togglePublishStatus(publishedAt),
});

const handleReportsError = (store: PublishingStore) => (error: PublishingError) => {
  store.error(error);
  warnAndNotify('PublishingReactions', error.kind, error);
};

const loadNewTemplate = (args: { resource: LearningCollectionPublishResource }) =>
  fromNullableT(args.resource.templates.self);

const loadNewCollectionResource = (link: Maybe<Link>, publishedAt: Maybe<Date>) =>
  toTask<PublishingError, Link>(missingLinkError({ rel: 'publish' }), link).andThen(
    callApi(
      learningCollectionPublishResourceDecoder,
      learningCollectionPublishingEncoder(publishedAt),
    ),
  );
const loadForNew = (link: Maybe<Link>, store: PublishingStore, publishedAt: Maybe<Date>) =>
  Task.succeed<PublishingError, {}>({})
    .assign('resource', loadNewCollectionResource(link, publishedAt))
    .assign('template', loadNewTemplate)
    .fork(handleReportsError(store), ({ resource, template }) =>
      store.ready(findLinkBy({ rel: 'publish' }, resource.links), resource, template),
    );

class PublishingReactions extends ReactionComponent<PublishingStore, PublishingState, Props> {
  tester = () => this.props.store.state;
  effect = (state: PublishingState) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
        break;
      case 'publishing':
        loadForNew(state.publishLink, store, state.publishing.payload.publishedAt);
        break;
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default PublishingReactions;
